import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  organizationStatusDisplay,
  relatedOrganizationsLinks,
  type OrganizationListRow,
  type OrganizationSortKey,
} from "../lib/organizations-ops";
import { cmsStrings } from "../terminology";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

const ui = cmsStrings.organizationsUi;
const inv = cmsStrings.inventory;

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: OrganizationSortKey;
  onSortChange: (value: OrganizationSortKey) => void;
};

export function OrganizationsFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: inv.filterAll },
    { id: "active", label: ui.statusActive },
    { id: "draft", label: ui.statusDraft },
    { id: "suspended", label: ui.statusSuspended },
    { id: "archived", label: ui.statusArchived },
  ];

  return (
    <FilterToolbar className="list-filter-bar announcements-filter-bar" aria-label={ui.filterAria}>
      <div className="filter-toolbar__chips announcements-filter-bar__chips" role="group" aria-label={ui.filterAria}>
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`filter-chip${filter === chip.id ? " filter-chip--active" : ""}`}
            aria-pressed={filter === chip.id}
            onClick={() => onFilterChange(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <label className="filter-toolbar__search">
        <span className="visually-hidden">{ui.searchAria}</span>
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={ui.searchPlaceholder}
          aria-label={ui.searchAria}
        />
      </label>
      <label className="filter-toolbar__sort">
        <span className="visually-hidden">{ui.sortAria}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as OrganizationSortKey)}
          aria-label={ui.sortAria}
        >
          <option value="name">{ui.sortName}</option>
          <option value="code">{ui.sortCode}</option>
          <option value="status">{ui.sortStatus}</option>
          <option value="updated">{inv.sortUpdated}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function OrganizationInspectSummary({
  item,
  isActive,
  actions,
}: {
  item: OrganizationListRow;
  isActive: boolean;
  actions?: ReactNode;
}) {
  const status = organizationStatusDisplay(item.status);
  const links = relatedOrganizationsLinks();

  return (
    <section className="content-inspect" aria-labelledby="org-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">{ui.inspectKind}</p>
          <h2 id="org-inspect-title" className="content-inspect__name">
            {item.displayName}
            {isActive ? ui.inspectActiveSuffix : ""}
          </h2>
          <p className="content-inspect__id">
            {inv.factId} <LtrIsolate mono>{item.id}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label={ui.colStatus}>
          <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
        </div>
      </header>

      {status.detail && <p className="content-inspect__eligibility-detail">{status.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>{ui.factCode}</dt>
          <dd>
            <LtrIsolate mono>{item.code}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>{ui.factCreated}</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.createdAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>{ui.factUpdated}</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.updatedAt)}</LtrIsolate>
          </dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label={ui.relatedAria}>
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function OrganizationsInventoryList({
  rows,
  selectedId,
  activeOrganizationId,
  onSelect,
  renderActions,
}: {
  rows: OrganizationListRow[];
  selectedId: string | null;
  activeOrganizationId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: OrganizationListRow) => ReactNode;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label={ui.tableAria}
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">{ui.colName}</th>
              <th scope="col">{ui.colCode}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">
                <span className="visually-hidden">{inv.details}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const status = organizationStatusDisplay(row.status);
              const isActive = row.id === activeOrganizationId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      {row.displayName}
                      {isActive ? ui.tableActiveSuffix : ""}
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.code}>
                      <LtrIsolate mono>{row.code}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        {inv.details}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label={ui.cardsAria}
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const status = organizationStatusDisplay(row.status);
          const isActive = row.id === activeOrganizationId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  {row.displayName}
                  {isActive ? ui.tableActiveSuffix : ""}
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{row.code}</LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
